import { useEffect, useState, useCallback } from "react";
import { fetchBoardComments } from "../../../utils/api/fetchBoardComment";
import { useParams } from "react-router-dom";
import { useBoard } from "../../../providers/BoardDetailProvider";
const LIMIT = 10;

export const useGetTabUUID = () => {
  const [tabUID, setTabUID] = useState<any>('')
  const generateGuid = () => {
    const S4 = () =>
      Math.floor(Math.random() * 0x10000)
        .toString(16)
        .padStart(4, "0");
    return `GUID-${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`;
  };

  const getTopMostWindow = useCallback(() => {
    let topMostWindow = window as Window;
    while (topMostWindow !== topMostWindow.parent) {
      topMostWindow = topMostWindow.parent;
    }
    return topMostWindow;
  }, []);

  useEffect(() => {
    const topMostWindow = getTopMostWindow();
    if (!topMostWindow?.name?.startsWith("GUID-")) {
      topMostWindow.name = generateGuid();
    }
    setTabUID(topMostWindow?.name)
  }, [getTopMostWindow]);
  return tabUID;
};

export const useConvertCommentTree = () => {
  return (comments: any) => {
    let temp: any = {};
    if (!Array.isArray(comments) || comments.length === 0) {
      return temp;
    }
    comments.forEach((comment: any) => {
      if (!comment) {
        return;
      }
      if (comment?.parent_id) {
        if (temp[comment.parent_id]) {
          temp[comment.parent_id].children = [
            ...(temp?.[comment.parent_id]?.children || []),
            comment,
          ];
        } else {
          temp[comment.parent_id] = { children: [comment] };
        }
        return;
      }
      if (temp[comment?.id]) {
        temp[comment?.id] = { ...(temp[comment?.id] ?? {}), ...comment };
      } else {
        temp[comment?.id] = { ...comment };
      }
    });
    return temp;
  };
};

export const useGetRootComments = () => {
  const { board_id } = useParams<{ board_id: string }>();
  const [isCommentLoading, setIsCommentLoading] = useState<boolean>(false);

  const { fetchComments } = useBoard();
  useEffect(() => {
    const getAllComments = async () => {
      if (!board_id) {
        return;
      }
      try {
        await fetchBoardComments({
          board_id: Number(board_id),
          limit: LIMIT,
        }).then((response) => {
          const { isSuccess } = response ?? {};
          if (!!isSuccess) {
            const {
              data: result = [],
              has_more = false,
              next_cursor = null,
            } = response?.result ?? {};

            if (Array.isArray(result) && result.length !== 0) {
              const hasPinnedComment = result.some(
                (comment: any) => !!comment?.is_pinned
              );
              const nextPageComments = result.map((comment: any) => ({
                ...comment,
              }));
              fetchComments(
                nextPageComments,
                has_more,
                next_cursor,
                hasPinnedComment
              );
            } else {
              fetchComments([], has_more, next_cursor);
            }
          }
        });
      } catch (err) {
      } finally {
        setIsCommentLoading(false);
      }
    };
    getAllComments();
  }, [board_id]);

  return { isCommentLoading };
};

export const useGetAllRepliesCommentByParentId = () => {
  const { board_id } = useParams<{ board_id: string }>();
  const [isLoadingReplies, setIsLoadingReplies] = useState<boolean>(false);
  const { fetchAllReplyComments } = useBoard();

  const handleGetAllReplies = async (parent_id: number, limit: number) => {
    setIsLoadingReplies(true);
    try {
      const response = await fetchBoardComments({
        board_id: Number(board_id),
        limit: limit,
        parent_id: parent_id,
      });
      const { isSuccess, result } = response ?? {};
      if (isSuccess) {
        const { data = [] } = result ?? {};
        if (Array.isArray(data) && data.length !== 0) {
          const allReplyComments = data.map((comment: any) => ({
            ...comment,
            children: [],
          }));
          fetchAllReplyComments(parent_id, allReplyComments);
        }
      }
    } catch (error) {
      console.log("Error fetching replies:", error);
    } finally {
      setIsLoadingReplies(false);
    }
  };

  return { isLoadingReplies, handleGetAllReplies };
};

export const useGetNextPage = () => {
  const { board_id } = useParams<{ board_id: string }>();
  const [isNextPageLoading, setIsNextPageLoading] = useState<boolean>(false);
  const {
    state: { hasMore, nextCursor },
    fetchComments,
  } = useBoard();
  const handleGetNextPage = async () => {
    setIsNextPageLoading(true);
    try {
      if (!hasMore) {
        return;
      }
      await fetchBoardComments({
        board_id: Number(board_id),
        limit: LIMIT,
        next_cursor: nextCursor,
      }).then((response) => {
        const { isSuccess } = response ?? {};
        if (!!isSuccess) {
          const {
            data: result = [],
            has_more = false,
            next_cursor = null,
          } = response?.result ?? {};

          if (Array.isArray(result) && result.length !== 0) {
            const nextPageComments = result.map((comment: any) => ({
              ...comment,
            }));
            fetchComments(nextPageComments, has_more, next_cursor);
          } else {
            fetchComments([], has_more, next_cursor);
          }
        }
      });
    } catch (error) {
    } finally {
      setIsNextPageLoading(false);
    }
  };

  return { isNextPageLoading, handleGetNextPage };
};
